import * as React from 'react';

import type { PageProps } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';

import { ExploreCurriculum } from 'stile-shared/src/components/2024/common/ExploreCurriculum';
import { IconSet } from 'stile-shared/src/components/2024/common/IconSet/IconSet';
import { Link } from 'stile-shared/src/components/2024/common/Link';
import PedagogyHeader from 'stile-shared/src/components/2024/common/PedagogyHeader';
import PedagogyWithinStileCard from 'stile-shared/src/components/2024/common/PedagogyWithinStileCard';
import { SubNav, SubNavItem } from 'stile-shared/src/components/2024/common/SubNav';
import { Grid } from 'stile-shared/src/components/2024/layout/Grid';
import { Section } from 'stile-shared/src/components/2024/layout/Section';

import { PageLayout } from 'templates/2024/PageLayout';

const ExamplesSection = styled<typeof Section>(Section)`
  margin-top: 0 !important;
  grid-column: span 12;

  .content {
    padding-top: 0 !important;
    margin-bottom: 80px;
    @media (max-width: 768px) {
      padding-top: 0 !important;
      margin-bottom: 69px !important;
      margin: 0 25px;
    }
  }

  position: relative;

  h6 {
    color: var(--stile-green-3);
    margin: 0;
    margin-bottom: 10px;
    grid-column: span 12;
    @media (max-width: 768px) {
      margin-bottom: 0 !important;
    }
  }

  .grid-layout {
    margin-top: 0 !important;
    grid-column: 1 / span 12;
  }

  > .grid-layout {
    align-items: center;
    margin-bottom: 150px;
    @media (max-width: 768px) {
      margin-bottom: 80px;
    }
  }
`;

export { Head } from 'templates/2024/Head';

function ThreeDimensionalLearningPage(props: PageProps) {
  const exploreCurriculumImage = (
    <StaticImage
      src="../../components/our-principles/images/teachersaying_quality_4.png"
      alt="Stacked documents with celestial diagram showing sun and orbiting planets"
      placeholder="blurred"
      layout="constrained"
      width={124}
      height={124}
    />
  );

  const subNavItems: SubNavItem[] = [
    {
      label: '5E Instructional Model',
      url: '/pedagogies/5e-instructional-model/',
    },
    {
      label: 'Phenomena-based Learning',
      url: '/pedagogies/phenomena-based-learning/',
    },
    {
      label: 'Three-dimensional Learning',
      url: '/pedagogies/three-dimensional-learning/',
      isActive: true,
    },
    {
      label: 'SOLO Taxonomy',
      url: '/pedagogies/solo-taxonomy/',
    },
    {
      label: 'Universal Design for Learning',
      url: '/pedagogies/universal-design/',
    },
    {
      label: 'Explicit Instruction',
      url: '/pedagogies/explicit-instruction/',
    },
    {
      label: 'Inquiry-based Learning',
      url: '/pedagogies/inquiry-based-learning/',
    },
    {
      label: 'Cognitive Load Theory',
      url: '/pedagogies/cognitive-load-theory/',
    },
    {
      label: 'Differentiated Instruction',
      url: '/pedagogies/differentiated-instruction/',
    },
  ];

  return (
    <PageLayout {...props}>
      <SubNav items={subNavItems} />

      <PedagogyHeader
        title="Three-dimensional learning"
        description="Students use Science & Engineering Practices (SEPs) to build Disciplinary Core Ideas (DCIs) and think with Cross‑cutting Concepts (CCCs)—exactly how NGSS frames science education."
        iconBullets={[
          {
            iconSrc: IconSet['lightonIcon0404'],
            title: 'Initially developed by',
            text: 'National Research Council, USA.',
          },
          {
            iconSrc: IconSet['conflictIcon0505'],
            title: 'Effectiveness',
            text: (
              <>
                A randomized control trial of 7th Grade classrooms found that students using
                three-dimensional curricula performed 7.3 percentage points better on an
                NGSS-aligned test than those that didn’t{' '}
                <Link url="https://files.eric.ed.gov/fulltext/ED634389.pdf" openInNewTab>
                  (Harris et al., 2022)
                </Link>
                .
              </>
            ),
          },
          {
            iconSrc: IconSet['glossaryIcon0705'],
            title: 'Further reading',
            text: (
              <Link
                url="https://nap.nationalacademies.org/catalog/13165/a-framework-for-k-12-science-education-practices-crosscutting-concepts"
                openInNewTab
              >
                NRC Framework for K‑12 Science Education (2012)
              </Link>
            ),
          },
          {
            iconSrc: IconSet['locationIcon0704'],
            title: "Where you'll find it in Stile",
            text: "Students explain, explore, and elaborate on Disciplinary Core Ideas (DCIs) through Science and Engineering Practices (SEPs) and Crosscutting Concepts (CCCs). All three dimensions are interwoven in Stile's lessons, activities and assessment tasks.",
          },
        ]}
      />

      <ExamplesSection>
        <h6>Examples within Stile</h6>
        <Grid>
          <PedagogyWithinStileCard
            id="modeling-compounds"
            title="Modeling compounds"
            url="http://stileapp.com/go/3d-example-1"
            description="Students develop and use models to understand the scale and proportion of atoms to explain how crystals of a compound form."
            subtitle={
              <div>
                <em>Modeling compounds</em> lesson
                <br />
                in the Elements and Compounds unit
              </div>
            }
            image={
              <StaticImage
                src="../../../../stile-shared/assets/images/pedagogies/3d_modeling.png"
                alt="Text: Model the lithium mining process at an atomic scale. Image: Diagram showing evaporation ponds with labels describing how the Sun evaporates water until lithium salts form, resulting in solid lithium chloride."
                objectFit="contain"
              />
            }
          />
          <PedagogyWithinStileCard
            id="continental-drift"
            title="Continental drift"
            description="Students analyze and interpret data to identify patterns in fossil distribution and seafloor structures as evidence of past plate motions."
            url="http://stileapp.com/go/3d-example-2"
            subtitle={
              <div>
                <em>Continental drift</em> lesson
                <br />
                in the Active Earth unit
              </div>
            }
            image={
              <StaticImage
                src="../../../../stile-shared/assets/images/pedagogies/3d_earth_science.png"
                alt="Text: Compare the fossil evidence to piece together the puzzle and show how the continents once fit together. Image: Map showing fossil distributions of several animals and plant across South America, Africa, India, and Australia."
                objectFit="contain"
              />
            }
          />
          <PedagogyWithinStileCard
            id="saving-corals"
            title="Saving corals"
            description="Students design solutions focusing on stability and change to monitor and increase beneficial human influences on reef environments."
            url="http://stileapp.com/go/3d-example-3"
            subtitle={
              <div>
                <em>Strategies for saving corals</em> lesson
                <br />
                in the Climate Change unit
              </div>
            }
            image={
              <StaticImage
                src="../../../../stile-shared/assets/images/pedagogies/3d_engineering.png"
                alt="Text: Design a replacement or solution for a plastic water bottle, sunscreen, or anchor. Image: Engineering challenge with illustrations of plastic and reusable bottles next to a photo of a coral reef with a turtle."
                objectFit="contain"
              />
            }
          />
        </Grid>
      </ExamplesSection>

      <ExploreCurriculum
        title="Explore our core science curriculum"
        image={exploreCurriculumImage}
        exploreCardsData={[
          {
            title: "Inside Stile's Curriculum",
            description: 'How we make the best science lessons in the known universe.',
            url: '/what-is-stile/middle-school-curriculum/',
          },
          {
            title: "What's Included",
            description:
              'A complete, comprehensive curriculum — used, loved, and perfected by thousands of teachers.',
            url: '/what-is-stile/whats-included/',
          },
          {
            title: 'Why HQIM?',
            description: 'Why High-Quality Instructional Materials make all the difference.',
            url: '/why-choose-stile/high-quality-instructional-materials/',
          },
        ]}
      />
    </PageLayout>
  );
}

export default ThreeDimensionalLearningPage;
